// Single source of truth for all services across the site
import {
  Target, ShoppingCart, Globe, Database, Package, Sparkles,
  TrendingUp, MousePointerClick, Megaphone, BarChart3, Users, Search,
  Code2, Smartphone, Layout, Zap, Layers, PenTool, Palette, Eye,
  Box, FileBox, Cpu, Workflow, LineChart, Award
} from 'lucide-react';

export const SERVICES = [
  {
    slug: 'performance-marketing',
    title: 'Performance Marketing',
    tagline: 'Paid media that pays back.',
    short: 'Conversion-obsessed paid campaigns across Meta, Google, YouTube and LinkedIn — built to scale ROAS.',
    accent: '#ff5a1f',
    icon: Target,
    og_image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&q=80&auto=format&fit=crop',
    keywords: 'performance marketing agency India, google ads agency, meta ads agency, paid media management, ROAS optimization, growth marketing, performance advertising',
    hero: {
      eyebrow: 'Performance Marketing',
      heading: 'Spend less. Sell more. Scale faster.',
      sub: 'We treat every rupee like equity. Full-funnel paid media powered by creative testing, audience science and live attribution.',
    },
    capabilities: [
      { icon: MousePointerClick, title: 'Google Ads & PMAX', desc: 'Search, Shopping, YouTube and Performance Max engineered for revenue.' },
      { icon: Megaphone, title: 'Meta & Instagram Ads', desc: 'Creative-led campaigns built around the Meta algorithm in 2026.' },
      { icon: Users, title: 'LinkedIn B2B Ads', desc: 'Account-based plays that fill enterprise pipelines.' },
      { icon: BarChart3, title: 'Conversion Tracking', desc: 'Server-side GA4, CAPI and dataLayer architected for accuracy.' },
      { icon: TrendingUp, title: 'Creative Testing Sprints', desc: 'Hook-led video and static iteration loops every 7 days.' },
      { icon: Search, title: 'Funnel & Landing CRO', desc: 'A/B tests on every step from impression to invoice.' },
    ],
    deliverables: [
      'Channel mix strategy & media plan',
      'Pixel + server-side tracking setup',
      'Weekly creative refreshes',
      'Audience and creative testing matrix',
      'Bi-weekly performance war-room',
      'Custom Looker Studio dashboard',
    ],
    metrics: [
      { value: '5.4x', label: 'Avg. ROAS lift' },
      { value: '−38%', label: 'CAC reduction' },
      { value: '90+', label: 'Brands scaled' },
    ],
  },
  {
    slug: 'ecommerce-development',
    title: 'E-commerce Development',
    tagline: 'Storefronts engineered to sell.',
    short: 'Shopify, WooCommerce and headless commerce builds optimised for speed, conversion and lifetime value.',
    accent: '#ff5a1f',
    icon: ShoppingCart,
    og_image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1200&q=80&auto=format&fit=crop',
    keywords: 'ecommerce development company India, shopify development agency, woocommerce experts, headless commerce, ecommerce website design, D2C agency',
    hero: {
      eyebrow: 'E-commerce Development',
      heading: 'Commerce that converts on every scroll.',
      sub: 'Shopify, WooCommerce, headless. We build buying experiences your customers actually finish.',
    },
    capabilities: [
      { icon: ShoppingCart, title: 'Shopify Plus Builds', desc: 'High-conversion themes, custom apps and checkout extensibility.' },
      { icon: Layers, title: 'Headless Commerce', desc: 'Next.js + Shopify / Medusa stacks for blazing performance.' },
      { icon: Workflow, title: 'WooCommerce Stores', desc: 'WordPress-native commerce with payment, shipping and tax automation.' },
      { icon: Zap, title: 'Speed & Core Web Vitals', desc: 'Sub-2s loads on mobile. SEO-ready out of the box.' },
      { icon: LineChart, title: 'CRO & Funnel Design', desc: 'PDP, cart and checkout tuned for AOV and conversion rate.' },
      { icon: Box, title: 'Integrations & ERP Sync', desc: 'CRM, WMS, accounting, marketplaces — all stitched together.' },
    ],
    deliverables: [
      'Custom theme / headless storefront',
      'Payment gateways & shipping rules',
      'Inventory + order automation',
      'Email/SMS marketing flows wired in',
      'GA4, Meta CAPI & pixel setup',
      'CMS training and handover',
    ],
    metrics: [
      { value: '+42%', label: 'Conversion lift' },
      { value: '1.8s', label: 'Avg. page load' },
      { value: '100+', label: 'Stores shipped' },
    ],
  },
  {
    slug: 'web-development',
    title: 'Web Development',
    tagline: 'Websites that look like 2030.',
    short: 'Bespoke websites and web apps engineered with React, Next.js and Node — designed to rank, convert and last.',
    accent: '#ff5a1f',
    icon: Globe,
    og_image: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=1200&q=80&auto=format&fit=crop',
    keywords: 'web development company India, custom website design, react development agency, nextjs developers, business website design, SEO website',
    hero: {
      eyebrow: 'Web Development',
      heading: 'Your website is your highest-paid salesperson.',
      sub: 'We build fast, SEO-ready, design-forward web experiences that turn visitors into pipeline.',
    },
    capabilities: [
      { icon: Layout, title: 'Marketing Websites', desc: 'Crafted in Figma, shipped in code. Pixel-tight.' },
      { icon: Code2, title: 'Custom Web Apps', desc: 'React, Next.js, Node, FastAPI — production-grade.' },
      { icon: Smartphone, title: 'Mobile-First Design', desc: 'Responsive systems built around real-device usage.' },
      { icon: Search, title: 'On-page SEO', desc: 'Schema, sitemaps, Core Web Vitals — handled.' },
      { icon: Cpu, title: 'CMS & Headless', desc: 'Sanity, Strapi, WordPress, Contentful integrations.' },
      { icon: Eye, title: 'Motion & Interaction', desc: 'GSAP and Framer Motion sequences that delight.' },
    ],
    deliverables: [
      'Discovery, wireframes & UI design',
      'Front-end and back-end development',
      'CMS setup with role-based access',
      'SEO audit + technical SEO fixes',
      'Analytics, GTM and event tracking',
      '60-day post-launch support',
    ],
    metrics: [
      { value: '95+', label: 'Lighthouse score' },
      { value: '4x', label: 'Lead-gen lift' },
      { value: '12d', label: 'Avg. ship time' },
    ],
  },
  {
    slug: 'custom-crm-development',
    title: 'Custom CRM Development',
    tagline: 'Sales OS built around your team.',
    short: 'Tailored CRM platforms that automate pipelines, unify customer data and replace eight tools with one.',
    accent: '#ff5a1f',
    icon: Database,
    og_image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&q=80&auto=format&fit=crop',
    keywords: 'custom CRM development India, bespoke CRM software, sales automation platform, CRM for SMB, CRM integration agency, workflow automation',
    hero: {
      eyebrow: 'Custom CRM Development',
      heading: 'Off-the-shelf CRMs cost more than they save.',
      sub: 'We build CRMs that match the exact way your team sells, supports and scales — with automations baked in.',
    },
    capabilities: [
      { icon: Database, title: 'Custom Pipelines', desc: 'Stages, fields and rules modelled on your real-world process.' },
      { icon: Workflow, title: 'Sales Automation', desc: 'Lead routing, scoring, follow-ups and SLA reminders.' },
      { icon: Users, title: 'Customer 360', desc: 'Unified profiles pulling from web, ads, calls and emails.' },
      { icon: BarChart3, title: 'Reporting & Forecasting', desc: 'Real-time dashboards exec teams actually use.' },
      { icon: Zap, title: 'Integrations', desc: 'WhatsApp, Gmail, Razorpay, ERP, Zapier — all native.' },
      { icon: Cpu, title: 'AI Assist', desc: 'Auto-summaries, draft replies and next-best-action prompts.' },
    ],
    deliverables: [
      'Process discovery & data modeling',
      'Custom CRM web app (with mobile-ready UI)',
      'Role-based permissions and audit logs',
      'Email, WhatsApp and call integration',
      'Reporting dashboards & exports',
      'Team onboarding & SOPs',
    ],
    metrics: [
      { value: '−60%', label: 'Manual tasks' },
      { value: '2.3x', label: 'Reply speed' },
      { value: '8→1', label: 'Tools replaced' },
    ],
  },
  {
    slug: 'package-designing',
    title: 'Package Designing',
    tagline: 'The first ad your product ever runs.',
    short: 'Packaging design and structural concepts that win shelves, unboxings and Instagram feeds.',
    accent: '#ff5a1f',
    icon: Package,
    og_image: 'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=1200&q=80&auto=format&fit=crop',
    keywords: 'packaging design agency India, product packaging design, D2C packaging, FMCG packaging, structural packaging, dieline design',
    hero: {
      eyebrow: 'Package Designing',
      heading: 'Make the box do the marketing.',
      sub: 'From dieline to delivery — packaging systems that turn customers into content creators.',
    },
    capabilities: [
      { icon: Package, title: 'Primary Packaging', desc: 'Boxes, pouches, bottles, jars — designed to stand out.' },
      { icon: FileBox, title: 'Secondary & Mailer Design', desc: 'Unboxing-first design for D2C and subscription brands.' },
      { icon: PenTool, title: 'Structural Engineering', desc: 'Dielines, mockups and pre-press files printers love.' },
      { icon: Award, title: 'Sustainable Materials', desc: 'Recyclable, compostable and minimalist material guidance.' },
      { icon: Layers, title: 'Range Architecture', desc: 'Coherent design systems across SKUs and variants.' },
      { icon: Eye, title: 'Photography Direction', desc: 'Hero imagery for Amazon, D2C and retail.' },
    ],
    deliverables: [
      'Concept moodboards & directions',
      'Print-ready dielines & artwork',
      'Material and finish recommendations',
      'Pre-press files for vendors',
      '3D mockups and renders',
      'Brand and packaging guidelines',
    ],
    metrics: [
      { value: '70+', label: 'SKUs designed' },
      { value: '3x', label: 'Shelf pickup rate' },
      { value: '24', label: 'Print partners' },
    ],
  },
  {
    slug: 'branding',
    title: 'Branding',
    tagline: 'A brand people would tattoo.',
    short: 'Identity systems, naming, visual language and brand strategy that make your business unmistakable.',
    accent: '#ff5a1f',
    icon: Sparkles,
    og_image: 'https://images.unsplash.com/photo-1561070791-2526d30994b8?w=1200&q=80&auto=format&fit=crop',
    keywords: 'branding agency India, brand identity design, logo design agency, brand strategy, rebranding services, brand guidelines',
    hero: {
      eyebrow: 'Branding',
      heading: 'Brands are not built in decks. They are built in feelings.',
      sub: 'We craft brand identities that earn attention, build trust and pay rent inside your customer\'s head.',
    },
    capabilities: [
      { icon: Sparkles, title: 'Brand Strategy', desc: 'Positioning, archetypes, voice and narrative frameworks.' },
      { icon: PenTool, title: 'Logo & Identity', desc: 'Marks, monograms and identity systems built to scale.' },
      { icon: Palette, title: 'Visual Language', desc: 'Type systems, palettes, iconography and motion.' },
      { icon: Layout, title: 'Brand Guidelines', desc: 'Living style guides your team can actually follow.' },
      { icon: Megaphone, title: 'Naming & Verbal Identity', desc: 'Names, taglines, tone of voice and copy systems.' },
      { icon: Eye, title: 'Launch Campaigns', desc: 'Brand-led launch films, OOH and social rollouts.' },
    ],
    deliverables: [
      'Brand strategy & positioning doc',
      'Logo system & lockups',
      'Color, type and iconography',
      'Brand voice and messaging',
      'Comprehensive brand guidelines',
      'Launch creative assets',
    ],
    metrics: [
      { value: '60+', label: 'Brands launched' },
      { value: '4 wks', label: 'Avg. timeline' },
      { value: '100%', label: 'In-house craft' },
    ],
  },
];

export const getServiceBySlug = (slug) => SERVICES.find((s) => s.slug === slug);
